import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { Button, Col, Collapse, Divider, Form, Row, Space, Steps, Tooltip } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import moment, { Moment } from "moment";
import { ReactComponent as PlusIcon } from "../../../img/plus.svg";
import { useAppDispatch, useAppSelector } from "../../..";
import { useOfferContext } from "../context/OfferContext";
import type {
  IOfferListSelectMode,
  IOfferParamsFormData,
  IOfferServiceProfileFormData,
} from "../context/types";
import type { ITenant, Offer, ServiceRate, ServiceType } from "../../../lib/types";
import {
  toHttpFormatStartOfDay,
  checkIsLoading,
  geDicValueById,
  round,
  checkIsLoaded,
} from "../../../lib/utils";
import LocalStorageService from "../../../services/LocalStorageService";
import { OfferParamsEdit } from "../../../components/OfferDetails/OfferParamsEdit/OfferParamsEdit";
import { ServiceProfileEdit } from "../../../components/OfferDetails/ServiceProfileEdit/ServiceProfileEdit";
import { validateOfferNameUniqueness } from "../utils/validateOfferNameUniqueness";
import { ServiceRateEdit } from "../../../components/common/ServiceRate/ServiceRateEdit/ServiceRateEdit";
import { FormInfo } from "../../../components/common/FormInfo/FormInfo";
import { createOffer } from "../../../store/slices/offering/offering";
import { CharacteristicsContainer } from "../../../modules/policies";
import { useCharacteristicsInitialisation } from "../../../modules/policies/pages/CharacteristicsContainer/utils/useCharacteristicsInitialisation";
import { IOfferListSearch } from "../../../store/slices/offering/types";
import { AggregationRulesEdit } from "../../../modules/AggregationRules";
import { AggregationRule } from "../../../lib/types/AggregationRule";
import { onAddPrice } from "../utils/onAddPrice";
import { onChangeTariffModelId } from "../utils/onChangeTariffModelId";
import { getOfferCreateBody } from "../utils/getOfferCreateBody/getOfferCreateBody";
import { parseRuleCondition } from "../utils/parseRuleCondition";
import { fakeBalanceType } from "../../../components/common/ServiceRate/utils/fakeBalanceType";
import { fetchTax } from "../../../store/slices/taxTs";
import "./OfferCreateContainer.less";

export const OfferCreateContainer = () => {
  const {
    offerParamsForm,
    tarifficationForm,
    aggregationRulesForm,
    getNewAggregationRule,
    serviceProfileForm,
    updateTaxRate,
    offerListSelectMode,
    selectedOffer,
    clearServiceProfile,
    searchListValues,
    setOfferListViewMode,
    setOfferListSelectMode,
    setSelectedOffer,
    onResetSearch,
    setIsFirstView,
    onChangeSearchValue,
    newRuleCondition,
  } = useOfferContext();

  const dispatch = useAppDispatch();
  const { offerLoading, serviceTypeList, serviceTypeListLoading } = useAppSelector(
    (state) => state.offering
  );
  const { tax } = useAppSelector((state) => state.taxTs);
  const { offeringStatuses } = useAppSelector((state) => state.dicts);

  // Список групп трафика для использования в форме тарификации. Нужен, т.к. в antd 4 отсуствует возможность получения значения в моменте
  const [aggregationRuleList, setAggregationRuleList] = useState<AggregationRule<"createForm">[]>();

  const history = useHistory();

  const tenant = useAppSelector((state) => state.tenants.tenant) as ITenant;

  const [checkedQueryParams, setCheckedQueryParams] = useState(false);
  const [checkedServiceTypeForQueryParams, setCheckedServiceTypeForQueryParams] = useState(false);

  useEffect(
    () => () => {
      offerParamsForm.resetFields();
      tarifficationForm.resetFields();
      aggregationRulesForm.resetFields();
      serviceProfileForm.resetFields();
    },
    []
  );

  const toOfferList = () => {
    history.push("/offer/list/create");
  };

  const defaultOfferingTypeId = 1;

  const [isCommonOfferingType, setIsCommonOfferingType] = useState(() => {
    if (offerListSelectMode === "basedOn") {
      return selectedOffer?.offeringTypeId === 1;
    }
    return true;
  });
  const [isPacketTariff, setIsPacketTariff] = useState(() => {
    if (offerListSelectMode === "basedOn") {
      return selectedOffer?.tariffModelId === 2;
    }
    return false;
  });

  // console.log({ creatingFormsData });

  // Исходные данные параметров предложения
  useEffect(() => {
    const initialValues: Partial<IOfferParamsFormData> =
      offerListSelectMode === "basedOn"
        ? {
            extName: selectedOffer?.extName,
            offeringTypeId: selectedOffer?.offeringTypeId,
            agreementId: selectedOffer?.agreementId,
            prolongTypeId: selectedOffer?.prolongTypeId,
            lifespanPeriodId: selectedOffer?.lifespanPeriodId,
            lifespanPeriodQty: selectedOffer?.lifespanPeriodQty,
            isStandalone: selectedOffer?.isStandalone,
            offeringStatusId: 1,
            tariffModelId: selectedOffer?.tariffModelId,
            bundleTypeId: selectedOffer?.bundleTypeId,
            periodAlignmentId: selectedOffer?.periodAlignmentId,
          }
        : {
            offeringTypeId: defaultOfferingTypeId,
            lifespanPeriodQty: 1,
            isStandalone: true,
            offeringStatusId: 1,
            tariffModelId: 1,
            bundleTypeId: 1,
          };
    offerParamsForm.setFieldsValue(initialValues);
  }, [offerListSelectMode, offerParamsForm, selectedOffer]);

  const serviceType = useMemo((): ServiceType | undefined => {
    const serviceTypeId =
      offerListSelectMode === "new" ? searchListValues.serviceTypeId : selectedOffer?.serviceTypeId;
    const type = geDicValueById(serviceTypeList, serviceTypeId);

    if (checkedQueryParams && checkIsLoaded(serviceTypeListLoading)) {
      setCheckedServiceTypeForQueryParams(true);
    }

    return type || undefined;
  }, [serviceTypeList, offerListSelectMode, checkedQueryParams, serviceTypeListLoading]);

  // Исходная загрузка ставки налога для отображения в таблице цен
  useEffect(() => {
    if (serviceType) {
      dispatch(
        fetchTax({
          serviceTypeId: serviceType.id,
          amount: 1, // Нужна только ставка налога, поэтому передается 1
          amountType: "NET",
          onDate: toHttpFormatStartOfDay(moment()),
        })
      );
    }
  }, [serviceType]);

  useEffect(() => {
    const savedSearchValues = LocalStorageService.getObject("offerCreateSearchValues");

    if (savedSearchValues && !checkedQueryParams) {
      const savedSearchValuesParse = JSON.parse(savedSearchValues) as {
        searchListValues: IOfferListSearch<Moment>;
        offerListSelectMode: IOfferListSelectMode;
        selectedOffer: Offer<string> | undefined;
      };

      setOfferListViewMode && setOfferListViewMode("createOffer");

      if (serviceType === undefined && savedSearchValues) {
        if (savedSearchValuesParse.selectedOffer) {
          setSelectedOffer(savedSearchValuesParse.selectedOffer);
        }
        if (savedSearchValuesParse.offerListSelectMode) {
          setOfferListSelectMode &&
            setOfferListSelectMode(savedSearchValuesParse.offerListSelectMode);
        }
        if (savedSearchValuesParse.searchListValues) {
          onChangeSearchValue &&
            onChangeSearchValue({
              ...savedSearchValuesParse.searchListValues,
              onDate: moment(savedSearchValuesParse?.searchListValues?.onDate),
            });
        }
      }
    }
    setCheckedQueryParams(true);
  }, [serviceType]);

  // Переадресация к списку предложений, при serviceType === undefined (происходит при обновлении страницы создания, т.к. serviceTypeId берется из результатов поиска)
  useEffect(() => {
    if (serviceType === undefined && checkedServiceTypeForQueryParams) {
      history.push("/offer/list/create");
    }
  }, [serviceType, checkedServiceTypeForQueryParams, history]);

  const serviceModeId = useMemo(() => {
    if (serviceType) {
      return serviceType.serviceModeId;
    }
    return 1;
  }, [serviceType]);

  const [tarifficationCollapseKeys, setTarifficationCollapseKeys] = useState<string | string[]>([]);

  // Исходные данные тарификации
  useEffect(() => {
    if (offerListSelectMode === "basedOn" && selectedOffer?.serviceRates) {
      const aggRulesServiceRates: ServiceRate<string>[] = [];
      const hideTsError = selectedOffer.aggregationRules?.forEach(({ serviceRates }) => {
        const hideTsError1 = serviceRates?.forEach((rate) => {
          aggRulesServiceRates.push(rate);
        });
      });
      const rates = [...(selectedOffer.serviceRates || []), ...(aggRulesServiceRates || [])];
      const initialServiceRates = rates.map((item) => ({
        ...item,
        fromDate: moment(item.fromDate),
        toDate: moment(item.toDate),
        balanceTypeId: item.balanceTypeId ? item.balanceTypeId : fakeBalanceType[0].id,
      }));
      if (initialServiceRates.length) {
        setTarifficationCollapseKeys(["1"]);
      }
      tarifficationForm.setFieldsValue({ serviceRates: initialServiceRates });
    } else {
      tarifficationForm.setFieldsValue({ serviceRates: undefined });
    }
  }, [offerListSelectMode, selectedOffer, serviceModeId]);

  // Исходные данные групп трафика
  useEffect(() => {
    if (offerListSelectMode === "basedOn" && selectedOffer?.aggregationRules) {
      const aggregationRules: AggregationRule<"createForm">[] = selectedOffer.aggregationRules.map(
        (item) => ({
          ...item,
          fromDate: item.fromDate ? moment(item.fromDate) : undefined,
          toDate: item.toDate ? moment(item.toDate) : undefined,
          ruleCondition: parseRuleCondition(item.ruleCondition),
        })
      );
      setAggregationRuleList(aggregationRules);
      aggregationRulesForm.setFieldsValue({ aggregationRules });
    } else {
      aggregationRulesForm.setFieldsValue({ aggregationRules: undefined });
    }
  }, [offerListSelectMode, selectedOffer]);

  // Исходные данные профиля оплаты
  useEffect(() => {
    // id указываем только для общих профилей
    const id =
      selectedOffer?.serviceProfile.profileTypeId === 1
        ? selectedOffer?.serviceProfile.id
        : undefined;
    const initialValues: Partial<IOfferServiceProfileFormData> =
      offerListSelectMode === "basedOn"
        ? {
            id,
            profileTypeId: selectedOffer?.serviceProfile.profileTypeId,
            paymentTypeId: selectedOffer?.serviceProfile.paymentTypeId,
            invoicePeriodId: selectedOffer?.serviceProfile.invoicePeriodId,
            prepaymentPeriodId: selectedOffer?.serviceProfile.prepaymentPeriodId,
            prepaymentToPayDays: selectedOffer?.serviceProfile.prepaymentToPayDays,
            billToPayDays: selectedOffer?.serviceProfile.billToPayDays,
            billPeriodId: selectedOffer?.serviceProfile.billPeriodId,
          }
        : { profileTypeId: defaultOfferingTypeId };
    serviceProfileForm.setFieldsValue(initialValues);
  }, [offerListSelectMode, selectedOffer]);

  // ---> Характеристики <---
  const {
    fromDateCharacteristic,
    onChangeFromDateCharacteristic,
    onCheckFieldsError,
    fieldsError,
    infoCharacteristic,
    charValuesCharacteristic,
    productCharValuesCharacteristic,
    setInfoCharacteristic,
    setCharValuesCharacteristic,
    setProductCharValuesCharacteristic,
  } = useCharacteristicsInitialisation({});
  // ---> Характеристики <---

  const onSubmitCreate = async () => {
    const [
      offerValues,
      tarifficationValues,
      aggregationRulesValues,
      serviceProfileValues,
    ] = await Promise.all([
      offerParamsForm.validateFields(),
      tarifficationForm.validateFields(),
      aggregationRulesForm.validateFields(),
      serviceProfileForm.validateFields(),
    ]);
    const isOfferNameUnique = await validateOfferNameUniqueness({
      name: offerValues.name,
      viewMode: "create",
    });
    if (!isOfferNameUnique) {
      offerParamsForm.setFields([
        {
          name: "name",
          errors: [""],
        },
      ]);
      return;
    }

    const isValid = onCheckFieldsError([
      "infoCharacteristic",
      "charValuesCharacteristic",
      "productCharValuesCharacteristic",
    ]);

    if (!isValid) {
      return;
    }

    const createdOffer = getOfferCreateBody({
      offerValues,
      aggregationRulesValues,
      tarifficationValues,
      serviceProfileValues,
      isCommonOfferingType,
      infoCharacteristic,
      charValuesCharacteristic,
      productCharValuesCharacteristic,
      serviceTypeId: serviceType?.id,
    });

    dispatch(createOffer(createdOffer)).then(({ payload }) => {
      if (payload) {
        const { id, fromDate } = payload as Offer;
        const onDateString = moment(fromDate).format("YYYY-MM-DD");
        setOfferListViewMode("view");
        setOfferListSelectMode(undefined);
        setSelectedOffer(undefined);
        onResetSearch();
        LocalStorageService.setObject("offerCreateSearchValues");
        history.push(`/offer/${id}?onDate=${onDateString}`);
        offerParamsForm.resetFields();
        tarifficationForm.resetFields();
        aggregationRulesForm.resetFields();
        serviceProfileForm.resetFields();
        setIsFirstView(true);
      }
    });
  };

  // Изменение зависимых полей при измененении типа предложения
  // Id типов "индивидуальный" предложения и профиля не совпадают, для этого синхронизация
  // а также удаление выбранного договора
  const onChangeOfferingTypeId = (id: number) => {
    offerParamsForm.setFieldsValue({ agreementId: undefined });
    // tarifficationForm.setFieldsValue({ tariffTypeId: id });
    clearServiceProfile && clearServiceProfile();
    const profileTypeId = id === 1 ? 1 : 2;
    serviceProfileForm.setFieldsValue({
      profileTypeId,
    });
    setIsCommonOfferingType(id === 1);
  };

  const handleAddPrice = useCallback(
    (e: MouseEvent<SVGSVGElement, globalThis.MouseEvent>) => {
      e.stopPropagation();
      setTarifficationCollapseKeys(["1"]);
      onAddPrice({
        form: tarifficationForm,
        taxRate: round(tax?.taxRate || 0),
        currencyId: tenant?.defaultCurrencyId,
        isPacketTariff,
      });
    },
    [isPacketTariff, tarifficationForm, tax, tenant]
  );

  return (
    <div className="offer-create-container">
      <h1>
        <Tooltip title="К списку предложений">
          <LeftOutlined onClick={toOfferList} className="offer-create-container__backBtn" />
        </Tooltip>
        Создать предложение для {serviceType?.name}
      </h1>
      <Divider className="offer-create-container__upperDivider" />
      <div className="offer-create-container__steps">
        <Row justify="space-between">
          <Col flex="auto">
            <Steps size="small" current={1}>
              <Steps.Step title="Выбор предложения" />
              <Steps.Step title="Параметры предложения" />
            </Steps>
          </Col>
          <Col className="offer-create-container__steps-btns">
            <Space size={20}>
              <Button onClick={toOfferList}>Назад</Button>
              <Button type="primary" onClick={onSubmitCreate}>
                Создать
              </Button>
            </Space>
          </Col>
        </Row>
        <Divider className="offer-create-container__lowerDivider" />
      </div>
      <Collapse bordered={false} defaultActiveKey="1">
        <Collapse.Panel
          key="1"
          header={
            <Row justify="space-between" className="offer-create-container__pageTitle">
              <h3>Параметры предложения</h3>
            </Row>
          }
          collapsible="disabled"
        >
          <Form form={offerParamsForm} labelAlign="left">
            <OfferParamsEdit
              viewMode="create"
              offer={selectedOffer}
              isLoading={checkIsLoading(offerLoading)}
              onChangeOfferingTypeId={onChangeOfferingTypeId}
              onNameBlur={validateOfferNameUniqueness}
              allowedOfferingStatuses={offeringStatuses}
              offerParamsForm={offerParamsForm}
              searchListValues={searchListValues}
              offerListSelectMode={offerListSelectMode}
              updateTaxRate={updateTaxRate}
              onChangeTariffModelId={(tariffModelTypeId) =>
                onChangeTariffModelId({
                  tariffModelTypeId,
                  offerParamsForm,
                  setIsPacketTariff,
                  tarifficationForm,
                })
              }
              onChangeFromDateCharacteristic={onChangeFromDateCharacteristic}
              isCommonOfferingType={isCommonOfferingType}
            />
          </Form>
        </Collapse.Panel>
      </Collapse>

      <CharacteristicsContainer
        objectTypeId={1043}
        mode="create"
        title="Характеристики Предложения"
        tableData={infoCharacteristic}
        setTableData={setInfoCharacteristic}
        dataSaved={offerListSelectMode === "basedOn" ? selectedOffer?.info : undefined}
        fromDateCharacteristic={fromDateCharacteristic}
        messageWarningNotFromDate="Необходимо выбрать Дату создания предложения"
        fieldsError={fieldsError}
      />
      <CharacteristicsContainer
        mode="create"
        title="Параметры доступности для Клиента"
        isInheritableObject
        isSelectedObjectId
        tableData={charValuesCharacteristic}
        setTableData={setCharValuesCharacteristic}
        dataSaved={offerListSelectMode === "basedOn" ? selectedOffer?.charValues : undefined}
        fromDateCharacteristic={fromDateCharacteristic}
        messageWarningNotFromDate="Необходимо выбрать Дату создания предложения"
        isDefaultDisabled
        disabledValidation
        fieldsError={fieldsError}
      />
      <CharacteristicsContainer
        objectTypeId={1032}
        mode="create"
        title="Параметры Предложения (характеристики Продукта)"
        isInheritableObject
        tableData={productCharValuesCharacteristic}
        setTableData={setProductCharValuesCharacteristic}
        dataSaved={offerListSelectMode === "basedOn" ? selectedOffer?.productCharValues : undefined}
        valuesTitle="Допустимые значения"
        fromDateCharacteristic={fromDateCharacteristic}
        messageWarningNotFromDate="Необходимо выбрать Дату создания предложения"
        disabledValidation
        fieldsError={fieldsError}
      />

      <Divider style={{ margin: 0 }} />
      <AggregationRulesEdit
        className="offer-create-container__aggregationRules"
        aggregationRulesForm={aggregationRulesForm}
        availableFields={["aggName", "periodTypeId", "rangeFrom", "rangeTo", "ruleCondition"]}
        requiredFields={["aggName", "rangeFrom", "periodTypeId"]}
        getNewAggregationRule={getNewAggregationRule}
        newRuleCondition={newRuleCondition}
        setAggregationRuleList={(rules) => {
          setAggregationRuleList(rules);
        }}
        hidePeriod
      />
      <Divider style={{ margin: 0 }} />
      <Collapse
        bordered={false}
        activeKey={tarifficationCollapseKeys}
        onChange={(keys) => {
          setTarifficationCollapseKeys(keys);
        }}
      >
        <Collapse.Panel
          key="1"
          header={
            <Row justify="space-between" className="offer-create-container__pageTitle">
              <h3>Тарификация</h3>
              <Tooltip title="Добавить цену">
                <PlusIcon onClick={handleAddPrice} className="serviceRate-edit__addButton" />
              </Tooltip>
            </Row>
          }
        >
          <Form form={tarifficationForm} labelAlign="left">
            <ServiceRateEdit
              form={tarifficationForm}
              isPacketTariff={isPacketTariff}
              aggregationRuleList={aggregationRuleList}
              onRemoveLastRate={() => {
                setTarifficationCollapseKeys([]);
              }}
            />
          </Form>
        </Collapse.Panel>
      </Collapse>
      <Divider style={{ margin: 0 }} />
      <Collapse bordered={false} defaultActiveKey="1">
        <Collapse.Panel
          key="1"
          header={
            <Row justify="space-between" className="offer-create-container__pageTitle">
              <h3>Профиль оплаты</h3>
            </Row>
          }
        >
          <Form form={serviceProfileForm} labelAlign="left">
            <ServiceProfileEdit
              clearServiceProfile={clearServiceProfile}
              viewMode="create"
              isCommonOfferingType={isCommonOfferingType}
              isPacketTariff={isPacketTariff}
            />
          </Form>
        </Collapse.Panel>
      </Collapse>
      <FormInfo
        getFormValues={() => ({
          offerValues: offerParamsForm.getFieldsValue(),
          tarifficationValues: tarifficationForm.getFieldsValue(),
          aggregationRulesValues: aggregationRulesForm.getFieldsValue(),
          serviceProfileValues: serviceProfileForm.getFieldsValue(),
        })}
        getCreateBody={() => {
          const offerValues = offerParamsForm.getFieldsValue();
          if (!offerValues.fromDate) {
            offerValues.fromDate = moment();
          }
          return getOfferCreateBody({
            offerValues,
            tarifficationValues: tarifficationForm.getFieldsValue(),
            aggregationRulesValues: aggregationRulesForm.getFieldsValue(),
            serviceProfileValues: serviceProfileForm.getFieldsValue(),
            isCommonOfferingType,
            infoCharacteristic,
            charValuesCharacteristic,
            productCharValuesCharacteristic,
            serviceTypeId: serviceType?.id,
          });
        }}
      />
    </div>
  );
};
